import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Eye, Pencil, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { api, errMsg, formatDate, formatPrice } from "@/lib/api";
import { Page } from "@/components/layout/Layout";
import { Loading, Empty } from "@/components/common/States";

const STATUS = { active: ["Yayında", "text-emerald-700"], inactive: ["Yayından kaldırıldı", "text-zinc-500"], removed: ["Yönetici kaldırdı", "text-red-700"], suspended: ["Hesap askıda", "text-red-700"] };

export default function MyListings() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ["my-listings"], queryFn: async () => (await api.get("/me/listings")).data });
  const refresh = () => qc.invalidateQueries({ queryKey: ["my-listings"] });
  const setStatus = async (id, status) => {
    try { await api.post(`/listings/${id}/status`, { status }); toast.success(status === "active" ? "İlan yayında" : "İlan yayından kaldırıldı"); refresh(); } catch (e) { toast.error(errMsg(e)); }
  };
  const del = async (id) => {
    try { await api.delete(`/listings/${id}`); toast.success("İlan silindi"); refresh(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Page>
      <div className="flex items-end justify-between">
        <h1 className="font-display text-4xl font-semibold">İlanlarım</h1>
        <Button asChild className="bg-[#0071e3] text-white" data-testid="my-new-listing"><Link to="/ilan-ver">Yeni ilan</Link></Button>
      </div>
      <div className="mt-8">
        {isLoading ? <Loading /> : !data.length ? <Empty title="Henüz ilanınız yok" /> : (
          <div className="overflow-x-auto panel">
            <table className="w-full text-sm" data-testid="my-listings-table">
              <thead className="text-left text-xs uppercase text-zinc-500"><tr><th className="p-3">İlan</th><th className="p-3">Fiyat</th><th className="p-3">Durum</th><th className="p-3">Oluşturma</th><th className="p-3">Görüntülenme</th><th className="p-3 text-right">İşlemler</th></tr></thead>
              <tbody>
                {data.map((l) => (
                  <tr key={l.id} className="border-t border-black/[0.08]" data-testid={`my-listing-row-${l.id}`}>
                    <td className="p-3"><Link to={`/ilan/${l.id}`} className="font-medium hover:text-[#0077ed]">{l.title}</Link><p className="text-xs text-zinc-500">{l.product_title}</p></td>
                    <td className="p-3 font-mono">{formatPrice(l.price, l.currency)}</td>
                    <td className={`p-3 ${STATUS[l.status]?.[1]}`} data-testid={`my-listing-status-${l.id}`}>{STATUS[l.status]?.[0]}</td>
                    <td className="p-3 text-zinc-500">{formatDate(l.created_at)}</td>
                    <td className="p-3 text-zinc-500"><Eye className="mr-1 inline h-3 w-3" />{l.views}</td>
                    <td className="p-3">
                      <div className="flex justify-end gap-2">
                        {l.status !== "removed" && (l.status === "active"
                          ? <Button size="sm" variant="outline" className="bg-transparent" data-testid={`unpublish-${l.id}`} onClick={() => setStatus(l.id, "inactive")}>Yayından kaldır</Button>
                          : <Button size="sm" variant="outline" className="bg-transparent" data-testid={`publish-${l.id}`} onClick={() => setStatus(l.id, "active")}>Yayınla</Button>)}
                        {l.status !== "removed" && <Button asChild size="sm" variant="outline" className="bg-transparent" data-testid={`edit-${l.id}`}><Link to={`/ilan/${l.id}/duzenle`}><Pencil className="h-3 w-3" /></Link></Button>}
                        <AlertDialog>
                          <AlertDialogTrigger asChild><Button size="sm" variant="outline" className="bg-transparent text-red-700" data-testid={`delete-${l.id}`}><Trash2 className="h-3 w-3" /></Button></AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader><AlertDialogTitle>İlan silinsin mi?</AlertDialogTitle><AlertDialogDescription>Bu işlem geri alınamaz.</AlertDialogDescription></AlertDialogHeader>
                            <AlertDialogFooter><AlertDialogCancel>Vazgeç</AlertDialogCancel><AlertDialogAction data-testid={`confirm-delete-${l.id}`} onClick={() => del(l.id)} className="bg-red-600">Sil</AlertDialogAction></AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Page>
  );
}
